// Per-cell save state: syncing → saved (fades) | error (kept until the next edit).
import { createStore } from "./store";

export type CellStatus = { status: "saving" | "saved" | "error"; message?: string; at: number };

export const saveStore = createStore<{ version: number; cells: Map<string, CellStatus> }>({ version: 0, cells: new Map() });

const key = (loanId: string, field: string) => `${loanId}:${field}`;

function update(fn: (m: Map<string, CellStatus>) => void) {
  saveStore.set((s) => {
    const cells = new Map(s.cells);
    fn(cells);
    return { version: s.version + 1, cells };
  });
}

export function markSaving(loanId: string, fields: string[]) {
  update((m) => fields.forEach((f) => m.set(key(loanId, f), { status: "saving", at: Date.now() })));
}

export function markSaved(loanId: string, fields: string[]) {
  const at = Date.now();
  update((m) => fields.forEach((f) => m.set(key(loanId, f), { status: "saved", at })));
  window.setTimeout(() => {
    update((m) =>
      fields.forEach((f) => {
        const c = m.get(key(loanId, f));
        if (c && c.status === "saved" && c.at === at) m.delete(key(loanId, f));
      }),
    );
  }, 1200);
}

export function markError(loanId: string, fields: string[], message: string) {
  update((m) => fields.forEach((f) => m.set(key(loanId, f), { status: "error", message, at: Date.now() })));
}

export function cellStatus(loanId: string, field: string): CellStatus | undefined {
  return saveStore.get().cells.get(key(loanId, field));
}

export function useSaveState() {
  return saveStore.use();
}

/** Global indicator: how many cells are currently saving / in error. */
export function useSaveSummary(): { saving: number; errors: number; lastSavedAt: number | null } {
  const s = saveStore.use();
  let saving = 0;
  let errors = 0;
  let last: number | null = null;
  s.cells.forEach((c) => {
    if (c.status === "saving") saving++;
    else if (c.status === "error") errors++;
    else if (c.status === "saved") last = Math.max(last ?? 0, c.at);
  });
  return { saving, errors, lastSavedAt: last };
}
